import {
  useCalendarQuery,
  useCalendarsForMemberExternalAccounts,
  useMemberExternalAccounts,
  useUser,
} from '@/hooks';
import {
  buildCalendarSourceCatalog,
  createCalendarRouteSearchParams,
  readCalendarRouteSelection,
} from '@/lib/calendar-sources';
import type { CalendarQuerySource } from '@vivport/backend/types';
import { useViewContextStore } from '@/store/viewContextStore';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarGrid } from './CalendarGrid';
import { CalendarToolbar } from './CalendarToolbar';
import { Notice } from '@/components/ui/notice';

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function startOfWeekMonday(value: Date): Date {
  const dayStart = startOfDay(value);
  const day = dayStart.getDay();
  const distanceFromMonday = (day + 6) % 7;
  dayStart.setDate(dayStart.getDate() - distanceFromMonday);
  return dayStart;
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

export function CalendarPage() {
  const { data: memberContext } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  useEffect(() => {
    useViewContextStore.getState().setSlot('calendar', { weekStartIso: weekStart.toISOString() });
    return () => useViewContextStore.getState().clearSlot('calendar');
  }, [weekStart]);
  const selectedMemberId = memberContext?.selectedMember.id ?? 0;
  const externalAccountsQuery = useMemberExternalAccounts(selectedMemberId);
  const calendarExternalAccountIds = useMemo(
    () =>
      (externalAccountsQuery.data?.items ?? [])
        .filter((item) => item.provider === 'GOOGLE' && item.isActive)
        .map((item) => item.id),
    [externalAccountsQuery.data?.items]
  );
  const calendarsQuery = useCalendarsForMemberExternalAccounts(calendarExternalAccountIds);

  const sourceCatalog = useMemo(() => {
    if (!memberContext) {
      return null;
    }
    return buildCalendarSourceCatalog({
      selectedMember: memberContext.selectedMember,
      calendarsByAccount: calendarsQuery.data,
    });
  }, [calendarsQuery.data, memberContext]);

  const routeSelection = useMemo(
    () => readCalendarRouteSelection(searchParams, sourceCatalog),
    [searchParams, sourceCatalog]
  );

  useEffect(() => {
    const canonicalParams = createCalendarRouteSearchParams(routeSelection);
    const currentQuery = searchParams.toString();
    const canonicalQuery = canonicalParams.toString();
    if (currentQuery !== canonicalQuery) {
      setSearchParams(canonicalParams, { replace: true });
    }
  }, [routeSelection, searchParams, setSearchParams]);

  const sources = useMemo(() => {
    if (!sourceCatalog) {
      return [];
    }
    return routeSelection.selectedMemberCalendarSourceIds
      .map((sourceId) => sourceCatalog.byId.get(sourceId))
      .filter((source): source is NonNullable<typeof source> => source !== undefined);
  }, [routeSelection.selectedMemberCalendarSourceIds, sourceCatalog]);

  const wireSources = useMemo(
    () =>
      sources.map(
        (source): CalendarQuerySource => ({
          kind: source.kind,
          memberId: source.memberId,
          isPrimary: true,
          ...(source.kind === 'SELECTED_MEMBER' && source.providerSelector
            ? {
              providerSelector: source.providerSelector,
            }
            : {}),
        })
      ),
    [sources]
  );

  const queryInput = useMemo(() => {
    if (wireSources.length === 0) {
      return null;
    }
    const rangeStart = startOfDay(weekStart);
    const rangeEnd = addDays(rangeStart, 7);
    return {
      rangeStart: rangeStart.toISOString(),
      rangeEnd: rangeEnd.toISOString(),
      showHubClashes: routeSelection.showHubClashes,
      pageSize: 200,
      sources: wireSources,
    };
  }, [routeSelection.showHubClashes, weekStart, wireSources]);

  const calendarQuery = useCalendarQuery(queryInput);
  const calendarItems = useMemo(() => calendarQuery.data?.items ?? [], [calendarQuery.data?.items]);

  const fullDetailEvents = useMemo(
    () => calendarItems.filter((item) => item.projection === 'FULL_DETAIL'),
    [calendarItems]
  );

  const busyOnlyOverlays = useMemo(
    () => calendarItems.filter((item) => item.projection === 'BUSY_ONLY'),
    [calendarItems]
  );

  const selectedCalendarSummary = sources.length === 0
    ? sourceCatalog?.defaultSource.label ?? 'Selected member'
    : sources.length === 1
      ? sources[0].label
      : `${sources.length} selected calendars`;
  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-medium tracking-tight">Calendar</h1>
        <p className="text-sm text-muted-foreground">Weekly view for selected calendars with optional hub clash overlays.</p>
      </div>

      <CalendarToolbar
        rangeStart={weekStart}
        selectedCalendarSummary={selectedCalendarSummary}
        showHubClashes={routeSelection.showHubClashes}
        onPrev={() => setWeekStart((current) => addDays(current, -7))}
        onNext={() => setWeekStart((current) => addDays(current, 7))}
        onToday={() => setWeekStart(startOfWeekMonday(new Date()))}
      />

      {calendarQuery.isError ? (
        <Notice tone="destructive" className="p-3">
          Failed to load calendar data for this week.
        </Notice>
      ) : null}

      {calendarQuery.isLoading ? (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">Loading calendar events...</div>
      ) : null}

      {calendarQuery.data?.nextCursor ? (
        <Notice tone="warning" className="p-3">
          Calendar results are truncated for this view. Narrow the time range to load all matching events.
        </Notice>
      ) : null}

      <CalendarGrid weekStart={weekStart} fullDetailEvents={fullDetailEvents} busyOnlyOverlays={busyOnlyOverlays} />
    </div>
  );
}
